/**
 * EntityLink — an inline reference to a Bower entity inside Bower's prose.
 *
 * These used to render as bold text with a faint grey underline, which in dark
 * mode is indistinguishable from ordinary `**bold**` emphasis. The link was
 * invisible: nothing said it was clickable, and nothing said it was a thing in
 * Bower rather than a page on the web.
 *
 * The affordance is now a deliberate underline + a half-step of extra weight +
 * (for entities) an etched icon — NOT colour. The ink stays ink: a paragraph
 * citing four sources shouldn't turn into four purple blocks. Weight is
 * `medium` (500), which reads as a link against body 400 while staying clearly
 * apart from markdown `**bold**` at 700 — those two being indistinguishable is
 * the original bug. The icon is the strongest signal ("this is a thing in
 * Bower") and it is what separates an entity link from an external one, which
 * gets a trailing `open_in_new` instead.
 *
 * Hovering after a beat opens a small preview (title, type, when it last
 * changed) rather than a bare tooltip repeating the link text. Click navigates;
 * cmd/ctrl-click is left to the browser, so it opens in a new tab like any
 * other anchor.
 */

import {
  Box,
  Link as ChakraLink,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { resolveArtifactDisplayTitle } from '@bower/shared/utils/artifactDisplayTitle';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';

import { useArtifact } from '../../../hooks/data/useArtifacts';
import { useProject } from '../../../hooks/data/useProjects';
import { ROUTES } from '../../../constants/routes';
import { trackLinkOpened } from '../../../hooks/entity/openLinkArtifact';
import { ENTITY_ICONS, getEntityDisplayName } from '../../../theme/entityIcons';
import { Glyph } from '../../ui/Glyph';
import { EntityActivityGlyph } from '../../ui/EntityActivityGlyph';
import { useEntityActivity } from '../../../stores/entityActivityStore';
import { formatTimeAgo } from '../../../utils/timeUtils';
import { MaterialIcon } from '../../ui/MaterialIcon';
import type { ParsedEntityHref } from './entityHref';

/** Long enough that skimming a paragraph doesn't fire a popover per link. */
const HOVER_DELAY_MS = 400;

/**
 * Inline icon geometry — `inline-block`, never `inline-flex`.
 *
 * An inline-flex box is an ATOMIC INLINE, and CSS does not propagate
 * text-decoration into the contents of an atomic inline. Wrapping this link's
 * contents in one drew the underline on the <a> and never on the text inside
 * it, so the link rendered completely undecorated — the exact bug this
 * component exists to fix, shipped twice. Nothing but looking at it catches
 * this; there is no error and no warning.
 *
 * Spacing is margin, not a gap or a space character: whitespace between the
 * icon and the label would be a line-break opportunity, and an icon stranded
 * at the end of a line reads as a bullet.
 */
const INLINE_ICON = {
  display: 'inline-block',
  verticalAlign: '-0.15em',
} as const;

/**
 * Fetches and renders the preview.
 *
 * Split out so the query hooks live BEHIND the popover's `isLazy` boundary:
 * this component doesn't exist until the pointer has rested on the link, so a
 * paragraph with eight references issues zero requests and — just as
 * importantly — an entity link can be rendered anywhere without dragging a
 * QueryClientProvider requirement along with it. Gating a hook with
 * `enabled: false` would still have called it on every render.
 */
function EntityPreview({ entity }: { readonly entity: ParsedEntityHref }) {
  const isCollection = entity.entityType === 'project';
  const artifactQuery = useArtifact(isCollection ? undefined : entity.id);
  const projectQuery = useProject(isCollection ? entity.id : undefined);
  const artifact = artifactQuery.data;
  const project = projectQuery.data;
  // Both hooks THROW on `!response.success`, and a 404 is the *designed*
  // denial for a restricted entity (rules/entity-acl.md). Reading only `.data`
  // left a trashed, deleted or access-denied entity previewing as "Loading…"
  // for ever — a spinner that never resolves is the worst kind of answer.
  const isError = isCollection ? projectQuery.isError : artifactQuery.isError;

  const preview = isCollection
    ? project && { title: project.name, type: 'Collection', updatedAt: project.updatedAt }
    : artifact && {
        // `title` is nullable — an attachment saved straight from upload has
        // none, and the preview would render an empty heading. The shared
        // resolver falls back to the filename, which is what every other
        // surface shows for the same row.
        title: resolveArtifactDisplayTitle(artifact.title, artifact.metadata),
        type: getEntityDisplayName(artifact.artifactType),
        updatedAt: artifact.updatedAt,
      };

  if (isError) {
    return (
      <Text fontSize="sm" color="text.muted">
        Can&rsquo;t open this item: it may have been deleted, or you may not have access.
      </Text>
    );
  }

  return (
    <PreviewBody entity={preview ?? null} fallbackType={getEntityDisplayName(entity.entityType)} />
  );
}

function PreviewBody({
  entity,
  fallbackType,
}: {
  readonly entity: { title: string; type: string; updatedAt?: string } | null;
  readonly fallbackType: string;
}) {
  return (
    <VStack align="start" spacing={0.5}>
      <Text fontSize="sm" fontWeight="medium" color="text.primary" noOfLines={2}>
        {entity?.title ?? 'Loading…'}
      </Text>
      <Text fontSize="xs" color="text.muted">
        {/* The real type, which the href alone couldn't give us: /notes/:id is
            shared by notes, protocols and protocol notes. */}
        {entity?.type ?? fallbackType}
        {entity?.updatedAt ? ` · edited ${formatTimeAgo(entity.updatedAt)}` : ''}
      </Text>
    </VStack>
  );
}

interface EntityLinkProps {
  readonly href: string;
  readonly entity: ParsedEntityHref;
  readonly children: ReactNode;
  readonly internalLinkPolicy?: InternalLinkPolicy;
}

export type InternalLinkPolicy = 'navigate' | 'label';

export function EntityLink({
  href,
  entity,
  children,
  internalLinkPolicy = 'navigate',
}: EntityLinkProps) {
  const glyph = ENTITY_ICONS[entity.entityType].glyph;
  // Store only (a link carries no row): a spreadsheet the agent names while it
  // imports spins here too. No provider needed, so links still render anywhere.
  const activity = useEntityActivity(entity.id);
  const mark = (
    <EntityActivityGlyph activity={activity} size="13px">
      <Glyph {...glyph} size="13px" gradient="warm" />
    </EntityActivityGlyph>
  );

  const label = (
    <>
      {/* Inherits currentColor, so the icon always matches the link. */}
      <Box as="span" mr="0.25em" sx={INLINE_ICON}>
        {mark}
      </Box>
      {children}
    </>
  );

  if (internalLinkPolicy === 'label') {
    return (
      <Text as="span" fontWeight="medium" color="text.primary">
        <Box as="span" mr="0.25em" sx={INLINE_ICON}>
          {mark}
        </Box>
        {children}
      </Text>
    );
  }

  return (
    <Popover trigger="hover" openDelay={HOVER_DELAY_MS} placement="top" isLazy>
      <PopoverTrigger>
        {/* No local link styling: colour, weight and underline all come from
            the app-wide anchor treatment in theme/styles.ts. Restyling anchors
            locally is what silently lost the cascade before. */}
        {entity.entityType === 'link' ? (
          // A link artifact leaves Bower in a new tab, never this one
          // (docs/planning/link-artifacts.md, D6): /links/:id resolves it there.
          <ChakraLink
            href={ROUTES.link(entity.id)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLinkOpened('chat')}
          >
            {label}
          </ChakraLink>
        ) : (
          <ChakraLink as={RouterLink} to={href}>
            {label}
          </ChakraLink>
        )}
      </PopoverTrigger>
      <Portal>
        {/* Portalled because the thread is an overflow-y container that would
            otherwise clip the panel. */}
        <PopoverContent maxW="280px" w="auto" bg="bg.elevated" borderColor="border.default">
          <PopoverBody px={3} py={2}>
            <EntityPreview entity={entity} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

/**
 * A link that leaves Bower. Same accent so links read as one family, but no
 * entity icon and a trailing `open_in_new` — the two must never be styled
 * identically, or "open this note" and "open this website" look the same.
 */
export function ExternalLink({ href, children }: { readonly href?: string; children: ReactNode }) {
  return (
    <ChakraLink href={href} isExternal>
      {children}
      <MaterialIcon name="open_in_new" boxSize={3} aria-hidden ml="0.25em" sx={INLINE_ICON} />
    </ChakraLink>
  );
}
